'use strict';

import { esc } from '../core/util.js';
import { wordBreaks, spansWords, isWholeWords, displayRangeToNorm } from './norm.js';

// ─── Search ───────────────────────────────────────────────────────────────────

// Y is a vowel (matching Umiaq, OneLook, Ingrid). One shared definition backs
// both the `#`/`@` search classes and the Vowelcy/Consonantcy tools, so flipping
// it can't leave the two disagreeing on Y — the drift copyqat has.
export const CONSONANTS = 'bcdfghjklmnpqrstvwxz';
export const VOWELS = 'aeiouy';
export function escapeRegex(s)        { return s.replace(/[.+*?^${}()|[\]\\]/g, '\\$&'); }
// Omitting `-` is load-bearing: it's what makes `[a-m]`/`[0-9]` ranges work.
// Re-adding it (it reads as an oversight) silently kills every range.
export function escapeRegexClass(s)   { return s.replace(/[\]\\^]/g, '\\$&'); }

// Wildcards buildSearchPattern recognizes — keep this list in sync with it.
export const SEARCH_WILDCARD_RE = /[*?#@[]/;
export function isLiteralQuery(query) { return query !== '' && !SEARCH_WILDCARD_RE.test(query); }

export function patternLengthRange(query) {
  const q = (query || '').normalize('NFC');
  let min = 0, star = false;
  for (let i = 0; i < q.length; i++) {
    if (q[i] === '*') { star = true; continue; }
    if (q[i] === '[') { const end = q.indexOf(']', i); if (end !== -1) i = end; }   // a class is one token — mirror buildSearchPattern's scan
    min++;
  }
  return { min, max: star ? Infinity : min };
}

// Two arms: the regex runs against both the entry's norm (accents + separators
// stripped) and its verbatim display, matching if either does — norm forgives
// separators (`theirs` finds "the IRS"); display requires a typed space/accent.
// `mode` is the match-extent mode: '' (anywhere), 'full' (whole entry),
// 'start'/'end' (anchored at that end), 'word' (whole words), or 'span'
// (crosses a word break); the two word-relative modes read an unspaced
// entry's words through `reader`, a SpacingReader, when one is given.
// `literal` treats the query as plain text (no wildcards) so Ctrl-F can
// share this exact matcher and stay in agreement with Search on what matches.
export function buildSearchPattern(query, mode = '', { literal = false, reader = null } = {}) {
  // Don't trim: a typed space is a literal that anchors to a word boundary in
  // the display arm. Re-adding `.trim()` reads as an oversight but silently
  // kills that — even an all-whitespace query is a real space search.
  const q = query.normalize('NFC');
  if (!q) return null;

  function customClass(body) {
    const expanded = body.replace(/#/g, CONSONANTS).replace(/@/g, VOWELS);
    if (expanded.startsWith('^')) return `[^${escapeRegexClass(expanded.slice(1))}]`;
    return `[${escapeRegexClass(expanded)}]`;
  }

  const tokens = [];
  for (let i = 0; i < q.length; i++) {
    const ch = q[i];
    if (literal)         tokens.push({ kind: 'literal', re: escapeRegex(ch) });
    else if (ch === '*') tokens.push({ kind: 'wild', re: '.*' });
    else if (ch === '?') tokens.push({ kind: 'wild', re: '[\\p{L}\\p{N}]' });
    else if (ch === '#') tokens.push({ kind: 'wild', re: `[${CONSONANTS}]` });
    else if (ch === '@') tokens.push({ kind: 'wild', re: `[${VOWELS}]` });
    else if (ch === '[') {
      const end = q.indexOf(']', i);
      if (end === -1) tokens.push({ kind: 'literal', re: '\\[' });
      else { tokens.push({ kind: 'wild', re: customClass(q.slice(i + 1, end)) }); i = end; }
    }
    else tokens.push({ kind: 'literal', re: escapeRegex(ch) });
  }

  // hlPat wraps each maximal run of literal tokens in a capture group so only
  // the fixed text highlights, never what `?`/`*` swallowed.
  let pat = '', hlPat = '', runOpen = false;
  const closeRun = () => { if (runOpen) { hlPat += ')'; runOpen = false; } };
  for (const tok of tokens) {
    pat += tok.re;
    if (tok.kind === 'literal') { if (!runOpen) { hlPat += '('; runOpen = true; } }
    else closeRun();
    hlPat += tok.re;
  }
  closeRun();

  const gated = mode === 'word' || mode === 'span';
  let filterRe, hlRe, globalRe;
  try {
    filterRe = new RegExp(anchorPattern(mode, pat),   'iu');
    hlRe     = new RegExp(anchorPattern(mode, hlPat), 'giud');
    globalRe = new RegExp(anchorPattern(mode, pat),   'giud');
  } catch {
    return null;   // invalid class (e.g. reversed range `[m-a]`) — no usable pattern
  }

  const tag = (ranges, coord) => ranges.map(r => ({ ...r, coord }));
  return {
    test(wlEntry) {
      if (!gated) {
        if (filterRe.test(wlEntry.norm)) return true;
        const d = wlEntry.display;
        return d != null && filterRe.test(d);
      }
      const d = wlEntry.display;
      return anyMatch(globalRe, wlEntry.norm, matchModeOk(mode, wlEntry, 'norm', reader))
          || (d != null && anyMatch(globalRe, d, matchModeOk(mode, wlEntry, 'display', reader)));
    },
    // Prefer the display arm's ranges (already in display coordinates); fall back
    // to the norm arm, whose coordinates projectRangesToDisplay maps at render.
    searchRanges(wlEntry) {
      const d = wlEntry.display;
      if (d != null) {
        const dispRanges = searchRangesFor(d, hlRe, matchModeOk(mode, wlEntry, 'display', reader));
        if (dispRanges.length) return tag(dispRanges, 'display');
      }
      return tag(searchRangesFor(wlEntry.norm, hlRe, matchModeOk(mode, wlEntry, 'norm', reader)), 'norm');
    },
    globalRe,
    hlRe,
  };
}

// Non-capturing, so a regex's `$N` backrefs keep their group numbers.
export function anchorPattern(mode, src) {
  if (mode === 'full')  return '^(?:' + src + ')$';
  if (mode === 'start') return '^(?:' + src + ')';
  if (mode === 'end')   return '(?:' + src + ')$';
  return src;
}

// The per-match gate for the word-relative modes, null for the rest. Breaks are
// read on the first match, not up front, so an entry the pattern rejects never
// costs a spacing-table lookup.
export function matchModeOk(mode, wlEntry, coord, reader = null) {
  if (mode !== 'word' && mode !== 'span') return null;
  let breaks = null;
  return m => {
    let start = m.index, end = start + m[0].length;
    if (coord === 'display') [start, end] = displayRangeToNorm(wlEntry, start, end);
    if (mode === 'span' && end - start < 2) return false;
    breaks ??= wordBreaks(wlEntry, reader);
    return mode === 'word'
      ? isWholeWords(breaks, wlEntry.norm.length, start, end)
      : spansWords(breaks, start, end);
  };
}

function anyMatch(re, text, matchOk) {
  re.lastIndex = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (matchOk(m)) return true;
    re.lastIndex = m.index + 1;   // an accepted match may overlap the rejected one — see searchRangesFor
  }
  return false;
}

export function searchRangesFor(text, hlRe, matchOk = null) {
  hlRe.lastIndex = 0;
  const ranges = [];
  let m;
  while ((m = hlRe.exec(text)) !== null) {
    if (matchOk && !matchOk(m)) {
      // Resume just past the rejected match's *start*, not its end: a match
      // that satisfies the gate can overlap the rejected one, and the normal
      // hop-past-the-match scan silently skips it.
      hlRe.lastIndex = m.index + 1;
      continue;
    }
    ranges.push(...groupSpansToRanges(m));
    if (m[0].length === 0) hlRe.lastIndex++;   // step past a zero-width match or loop forever
  }
  return ranges;
}

// Without the `d` (indices) flag on the regex, `m.indices` is absent and this
// silently yields no highlights — the caller must compile with `d`.
// Must equal the count of --hl0..N CSS vars / .search-match-N rules; a mismatch
// emits search:N kinds with no matching color.
export const HL_COLORS = 9;

// Interpolating the kind per range allocates a fresh string for one of nine values,
// on every match of every highlighted scan. Indexing a shared table costs a pointer.
export const SEARCH_KINDS = Object.freeze(
  Array.from({ length: HL_COLORS }, (_, i) => `search:${i}`));

export function groupSpansToRanges(m) {
  if (!m?.indices) return [];
  const ranges = [];
  let colorIdx = 0;
  for (let g = 1; g < m.indices.length; g++) {
    if (!m.indices[g]) continue;
    const [start, end] = m.indices[g];
    ranges.push({ start, end, kind: SEARCH_KINDS[colorIdx % HL_COLORS] });
    colorIdx++;
  }
  return ranges;
}

// Render a string with a set of highlight ranges. Each range is
// `{ start, end, kind }`; search hits use `search:N` (rendered as `<mark>`),
// tool-emitted highlights use one of the kinds in the registry below
// (rendered as `<span class="hl-<kind>">`). Overlapping ranges are resolved
// by skipping later ranges entirely — simple and predictable; the visual
// loss is bounded to the rare case where a tool highlight overlaps a search
// match exactly. Entry text is HTML-escaped: this output is interpolated into
// innerHTML and an entry's display can come from a wordlist imported or fetched
// from an untrusted URL, so an unescaped `<img onerror>` entry would be XSS.
export function renderHighlightedText(text, ranges) {
  if (!ranges || !ranges.length) return esc(text);
  // On a tie, a find hit wins the slot over a co-starting search mark (current
  // match first), so a find never navigates to a row whose highlight is masked.
  const sorted = [...ranges].sort((a, b) => a.start - b.start || findRank(a) - findRank(b));
  let result = '';
  let pos = 0;
  for (const r of sorted) {
    if (r.start < pos || r.end <= r.start) continue;
    result += esc(text.slice(pos, r.start));
    const content = esc(text.slice(r.start, r.end));
    if (r.kind === 'find' || r.kind === 'find-current') {
      result += `<mark class="find-hit${r.kind === 'find-current' ? ' find-current' : ''}">${content}</mark>`;
    } else if (r.kind.startsWith('search:')) {
      result += `<mark class="search-match search-match-${r.kind.slice(7)}">${content}</mark>`;
    } else {
      result += `<span class="hl-${r.kind}">${content}</span>`;
    }
    pos = r.end;
  }
  return result + esc(text.slice(pos));
}

function findRank(r) {
  return r.kind === 'find-current' ? 0 : r.kind === 'find' ? 1 : 2;
}
